import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Collapse, FormCheck, Modal } from 'react-bootstrap';

import './standard-work-trainer.css';

import { DateRangeDropdown, DateRange, DateRangeDropdownHandle } from '../Filters/DateRangeDropdown';
import SearchableStringListDropdown, { SearchableStringListDropdownHandle } from '../Filters/SearchableStringListDropdown';
import WordSearchInput, { WordSearch, WordSearchInputHandle } from '../Filters/WordSearchInput';

import ChevronContract from '../Icons/Chevron/ChevronContract';
import ChevronDown from '../Icons/Chevron/ChevronDown';
import ChevronExpand from '../Icons/Chevron/ChevronExpand';
import CircleX from '../Icons/CircleX';
import ResetFilter from '../Icons/ResetFilter';

import { Sort } from '../../enums/Sort';
import { GENERAL_SUPERINTENDENT, SWT_ELIGIBLE_JOB_CODES } from '../../constants/JobCodes';

import { certNameMatches } from '../../functions/CertificationHelpers';
import { normalizeText } from '../../functions/TextHelpers';
import { isIsoDateInRange } from '../../functions/TimeHelpers';

import { certifyEmployee, removeEmployeeCertification, updateEmployeeCertification } from '../../services/Workday/WorkdayCertificationService';
import EmployeeService from '../../services/EmployeeService';
import { errorToast, infoToast, successToast, warningToast } from '../Toasty';

import EmployeeDetail from '../../types/Employee/EmployeeDetail';
import { WorkdayCertification } from '../../types/Workday/WorkdayCertification';
import { WorkdayEmployeeCertification } from '../../types/Workday/WorkdayEmployeeCertification';
import IProject from '../../types/Dashboard/Project';

interface EmployeeGroup {
    employee: EmployeeDetail;
    certs: WorkdayEmployeeCertification[];
}

const DEFAULT_FILTER: WordSearch = { value: '', regex: undefined, sort: Sort.Ascending };


const findProjectName = (projects: IProject[], locationId: string): string => {
    const match = projects.find(p => p.projectNumber === locationId)
        ?? projects.find(p => locationId.endsWith(p.projectNumber));
    return match?.projectName ?? '—';
};

type Props = {
    employeeCertList: WorkdayEmployeeCertification[];
    allWorkdayCertifications?: WorkdayCertification[];
    projectList?: IProject[];
    userCanAdd?: boolean;
    userCanEdit?: boolean;
    userEmail?: string;
    isMocked?: boolean;
    onSaveSuccess?: () => Promise<void>;
};

const StandardWorkTrainerTable = ({
    employeeCertList,
    allWorkdayCertifications = [],
    projectList = [],
    userCanAdd = false,
    userCanEdit = false,
    userEmail = '',
    isMocked = false,
    onSaveSuccess,
}: Props) => {
    const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
    const [expandedCertKeys, setExpandedCertKeys] = useState<Set<number>>(new Set());
    const [showFilters, setShowFilters] = useState(false);

    const [employeeFilter, setEmployeeFilter] = useState<WordSearch>(DEFAULT_FILTER);
    const [jobNameFilter, setJobNameFilter] = useState<WordSearch>(DEFAULT_FILTER);
    const [jobNumberFilter, setJobNumberFilter] = useState<string | undefined>(undefined);
    const [certFilter, setCertFilter] = useState<string | undefined>(undefined);
    const [certifiedByFilter, setCertifiedByFilter] = useState<string | undefined>(undefined);
    const [gsList, setGsList] = useState<ReadonlyArray<string>>([]);
    const [issuedDateFilter, setIssuedDateFilter] = useState<DateRange>({});
    const [expirationDateFilter, setExpirationDateFilter] = useState<DateRange>({});

    const [batchModalOpen, setBatchModalOpen] = useState(false);
    const [batchEmployeeSearch, setBatchEmployeeSearch] = useState('');
    const [batchCertSearch, setBatchCertSearch] = useState('');
    const [batchSelectedEmployees, setBatchSelectedEmployees] = useState<Map<string, EmployeeDetail>>(new Map());
    const [batchAdResults, setBatchAdResults] = useState<EmployeeDetail[]>([]);
    const [batchSelectedCerts, setBatchSelectedCerts] = useState<Set<number>>(new Set());
    const [batchIssueDate, setBatchIssueDate] = useState('');
    const [batchIsOneYear, setBatchIsOneYear] = useState(true);
    const [batchIsEvergreen, setBatchIsEvergreen] = useState(false);
    const [batchExpiryDate, setBatchExpiryDate] = useState('');
    const [batchCertifiedById, setBatchCertifiedById] = useState<number | null>(null);

    const [saving, setSaving] = useState(false);
    const [updateConfirmItems, setUpdateConfirmItems] = useState<{ empName: string; certName: string; oldExpiry: string; newExpiry: string }[]>([]);

    const [manageEmployee, setManageEmployee] = useState<EmployeeGroup | null>(null);
    const [manageCertEdits, setManageCertEdits] = useState<Map<number, { issuedDate: string; expiryDate: string; remove: boolean }>>(new Map());
    const [manageAddIds, setManageAddIds] = useState<Set<number>>(new Set());
    const [manageAddSearch, setManageAddSearch] = useState('');
    const [manageAddIssuedDate, setManageAddIssuedDate] = useState('');
    const [manageAddIsOneYear, setManageAddIsOneYear] = useState(true);
    const [manageAddIsEvergreen, setManageAddIsEvergreen] = useState(false);
    const [manageAddExpiryDate, setManageAddExpiryDate] = useState('');
    const [manageAddCertifiedById, setManageAddCertifiedById] = useState<number | null>(null);

    const batchEmployeeSearchRef = useRef('');
    const employeeRef = useRef<WordSearchInputHandle>(null);
    const jobNameRef = useRef<WordSearchInputHandle>(null);
    const jobRef = useRef<SearchableStringListDropdownHandle>(null);
    const certRef = useRef<SearchableStringListDropdownHandle>(null);
    const certifiedByRef = useRef<SearchableStringListDropdownHandle>(null);
    const batchGsRef = useRef<SearchableStringListDropdownHandle>(null);
    const manageGsRef = useRef<SearchableStringListDropdownHandle>(null);
    const issuedDateRef = useRef<DateRangeDropdownHandle>(null);
    const expirationDateRef = useRef<DateRangeDropdownHandle>(null);

    const employeeGroups = useMemo((): EmployeeGroup[] => {
        const map = new Map<string, EmployeeGroup>();
        for (const ec of employeeCertList) {
            const key = ec.employee.employeeID;
            if (!map.has(key)) {
                map.set(key, { employee: ec.employee, certs: [ec] });
            } else {
                map.get(key)!.certs.push(ec);
            }
        }
        return Array.from(map.values());
    }, [employeeCertList]);

    const jobNumberList = useMemo(() =>
        Array.from(new Set(employeeCertList.map(ec => ec.employee.employeeLocation)))
    , [employeeCertList]);

    const certNameList = useMemo(() =>
        allWorkdayCertifications
            .map(c => c.isActive ? c.name : `${c.name} (inactive)`)
            .sort((a, b) => a.localeCompare(b))
    , [allWorkdayCertifications]);

    const filteredGroups = useMemo((): EmployeeGroup[] => {
        const nameFilterVal = employeeFilter.value?.trim().toLowerCase() ?? '';

        return employeeGroups
            .filter(group => {
                const hasCertMatch = group.certs.some(ec => {
                    const certFilterName = certFilter?.replace(/ \(inactive\)$/, '') ?? '';
                    const matchesCert = certFilter
                        ? certNameMatches(ec.certification.name, certFilterName)
                        : true;
                    const certifiedByLabel = ec.certifiedBy
                        ? `${ec.certifiedBy.employeeName} (${ec.certifiedBy.employeeID})`
                        : null;
                    const matchesCertifiedBy = certifiedByFilter
                        ? certifiedByLabel === certifiedByFilter
                        : true;
                    const matchesIssued = isIsoDateInRange(ec.issuedDate, issuedDateFilter.start, issuedDateFilter.end);
                    const matchesExpiry = isIsoDateInRange(ec.expirationDate, expirationDateFilter.start, expirationDateFilter.end);
                    return matchesCert && matchesCertifiedBy && matchesIssued && matchesExpiry;
                });
                if (!hasCertMatch) return false;

                const name = normalizeText(group.employee.employeeName).replace(/,/g, '');
                const nameParts = name.split(' ').filter(n => n !== '(admin)');
                const nameMatch = nameParts.some(part => part.startsWith(nameFilterVal));
                const employeeID = normalizeText(group.employee.employeeID ?? '');

                const matchesEmployee = employeeFilter.regex
                    ? nameMatch || employeeFilter.regex.test(employeeID)
                    : true;
                const projectName = findProjectName(projectList, group.employee.employeeLocation);
                const matchesJobName = jobNameFilter?.regex
                    ? jobNameFilter.regex.test(normalizeText(projectName))
                    : true;
                const matchesJobNumber = jobNumberFilter
                    ? group.employee.employeeLocation === jobNumberFilter
                    : true;

                return matchesEmployee && matchesJobName && matchesJobNumber;
            })
            .sort((a, b) => {
                const nameA = normalizeText(a.employee.employeeName);
                const nameB = normalizeText(b.employee.employeeName);
                if (employeeFilter.sort !== undefined) {
                    const cmp = nameA.localeCompare(nameB);
                    return employeeFilter.sort === Sort.Ascending ? cmp : -cmp;
                }
                return nameA.localeCompare(nameB);
            });
    }, [employeeGroups, employeeFilter, jobNameFilter, jobNumberFilter, certFilter, certifiedByFilter, issuedDateFilter, expirationDateFilter]);

    const toggleExpand = (key: string) => {
        setExpandedKeys(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const toggleCertExpand = (id: number) => {
        setExpandedCertKeys(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const onRowKeyDown: React.KeyboardEventHandler<HTMLTableRowElement> = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleExpand((e.currentTarget as HTMLElement).dataset.key!);
        }
    };

    const resetFilters = () => {
        employeeRef.current?.clear();
        jobNameRef.current?.clear();
        jobRef.current?.clear();
        certRef.current?.clear();
        certifiedByRef.current?.clear();
        issuedDateRef.current?.clear();
        expirationDateRef.current?.clear();
        setEmployeeFilter(DEFAULT_FILTER);
        setJobNameFilter(DEFAULT_FILTER);
        setJobNumberFilter(undefined);
        setCertFilter(undefined);
        setCertifiedByFilter(undefined);
        setIssuedDateFilter({});
        setExpirationDateFilter({});
    };

    const expandAll = () => setExpandedKeys(new Set(filteredGroups.map(g => g.employee.employeeID)));
    const collapseAll = () => setExpandedKeys(new Set());

    const calcExpiry = (issueDate: string, years: number): string => {
        const d = new Date(issueDate);
        d.setFullYear(d.getFullYear() + years);
        return d.toISOString().split('T')[0];
    };

    const openBatchModal = () => {
        const today = new Date().toISOString().split('T')[0];
        setBatchEmployeeSearch('');
        setBatchCertSearch('');
        setBatchAdResults([]);
        setBatchSelectedEmployees(new Map());
        setBatchSelectedCerts(new Set());
        setBatchIssueDate(today);
        setBatchIsOneYear(true);
        setBatchIsEvergreen(false);
        setBatchExpiryDate(calcExpiry(today, 1));
        setBatchCertifiedById(null);
        batchGsRef.current?.clear();
        setBatchModalOpen(true);
    };

    const handleBatchSetOneYear = () => {
        setBatchIsOneYear(true);
        setBatchIsEvergreen(false);
    };

    const handleBatchSetEvergreen = () => {
        setBatchIsOneYear(false);
        setBatchIsEvergreen(true);
    };

    useEffect(() => {
        EmployeeService.getEmployeesByJobCode(GENERAL_SUPERINTENDENT, 500)
            .then(res => {
                const formatted = res.data
                    .map(e => `${e.employeeName} (${e.employeeID})`)
                    .sort((a, b) => a.localeCompare(b));
                setGsList(formatted);
            })
            .catch((err) => { errorToast('Failed to load the Certified By list. Please refresh the page.', { detail: String(err) }); });
    }, []);

    useEffect(() => {
        if (!batchIssueDate) return;
        setBatchExpiryDate(calcExpiry(batchIssueDate, batchIsEvergreen ? 99 : 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [batchIssueDate, batchIsOneYear, batchIsEvergreen]);

    const openManageModal = (group: EmployeeGroup) => {
        const today = new Date().toISOString().split('T')[0];
        const edits = new Map<number, { issuedDate: string; expiryDate: string; remove: boolean }>();
        for (const ec of group.certs) {
            edits.set(ec.id, {
                issuedDate: ec.issuedDate ? ec.issuedDate.slice(0, 10) : '',
                expiryDate: ec.expirationDate ? ec.expirationDate.slice(0, 10) : '',
                remove: false,
            });
        }
        setManageCertEdits(edits);
        setManageAddIds(new Set());
        setManageAddSearch('');
        setManageAddIssuedDate(today);
        setManageAddIsOneYear(true);
        setManageAddIsEvergreen(false);
        setManageAddExpiryDate(calcExpiry(today, 1));
        setManageAddCertifiedById(null);
        manageGsRef.current?.clear();
        setManageEmployee(group);
    };

    useEffect(() => {
        if (!manageAddIssuedDate) return;
        setManageAddExpiryDate(calcExpiry(manageAddIssuedDate, manageAddIsEvergreen ? 99 : 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [manageAddIssuedDate, manageAddIsOneYear, manageAddIsEvergreen]);

    const handleBatchSave = async () => {
        if (isMocked) {
            infoToast('You have clicked on the "Save" button.  This has been disabled because you are currently not yourself.');
            return;
        }

        const pairs: { empId: string; certId: number }[] = [];
        batchSelectedEmployees.forEach((_, empId) =>
            batchSelectedCerts.forEach(certId =>
                pairs.push({ empId, certId })
            )
        );

        // Split pairs: if the employee already holds this cert and the new expiry is later, update instead of add
        const toAdd: { empId: string; certId: number }[] = [];
        const toUpdate: { empId: string; certId: number; existingId: number; oldExpiry: string; rowVersion: string }[] = [];

        for (const pair of pairs) {
            const existing = employeeCertList.find(
                ec => ec.employee.employeeID === pair.empId && ec.certification.id === pair.certId
            );
            if (existing) {
                toUpdate.push({ ...pair, existingId: existing.id, oldExpiry: (existing.expirationDate ?? '').slice(0, 10), rowVersion: existing.rowVersion });
            } else {
                toAdd.push(pair);
            }
        }

        setSaving(true);
        try {
            const [addResults, updateResults] = await Promise.all([
                Promise.all(toAdd.map(({ empId, certId }) =>
                    certifyEmployee(parseInt(empId), certId, batchIssueDate, batchExpiryDate, batchCertifiedById!, userEmail)
                )),
                Promise.all(toUpdate.map(({ existingId, rowVersion }) =>
                    updateEmployeeCertification(existingId, batchIssueDate, batchExpiryDate, userEmail, rowVersion, batchCertifiedById)
                )),
            ]);

            const taggedAdds = addResults.map((r, i) => ({ r, pair: toAdd[i] }));
            const taggedUpdates = updateResults.map((r, i) => ({ r, item: toUpdate[i] }));

            const succeeded = taggedAdds.filter(({ r }) => r.status >= 200 && r.status < 300);
            const conflicts = taggedAdds.filter(({ r }) => r.status === 409);
            const failedAdds = taggedAdds.filter(({ r }) => r.status !== 409 && (r.status < 200 || r.status >= 300));
            const updateConflicts = taggedUpdates.filter(({ r }) => r.status === 409);
            const failedUpdates = taggedUpdates.filter(({ r }) => r.status !== 409 && (r.status < 200 || r.status >= 300));
            const failed = [...failedAdds.map(({ r }) => r), ...failedUpdates.map(({ r }) => r)];
            const updatesSucceeded = taggedUpdates.filter(({ r }) => r.status >= 200 && r.status < 300);

            if (succeeded.length > 0) {
                const detail = succeeded.map(({ pair }) => {
                    const empName = batchSelectedEmployees.get(pair.empId)?.employeeName ?? pair.empId;
                    const certName = allWorkdayCertifications.find(c => c.id === pair.certId)?.name ?? String(pair.certId);
                    return `${empName} – ${certName}`;
                }).join('\n');
                successToast(
                    `${succeeded.length} certification${succeeded.length !== 1 ? 's' : ''} saved.`,
                    { autoClose: false, detail }
                );
            }

            if (conflicts.length > 0) {
                const items = conflicts.map(({ pair }) => {
                    const empName = batchSelectedEmployees.get(pair.empId)?.employeeName ?? pair.empId;
                    const certName = allWorkdayCertifications.find(c => c.id === pair.certId)?.name ?? String(pair.certId);
                    return `${empName} – ${certName}`;
                });
                infoToast(
                    <div>
                        <div>{conflicts.length === 1 ? '1 certification already exists:' : `${conflicts.length} certifications already exist:`}</div>
                        <ul className='mb-0 ps-3'>
                            {items.map((item, i) => <li key={i}>{item}</li>)}
                        </ul>
                    </div>,
                    { autoClose: false }
                );
            }

            if (updateConflicts.length > 0) {
                const items = updateConflicts.map(({ item }) => {
                    const empName = batchSelectedEmployees.get(item.empId)?.employeeName ?? item.empId;
                    const certName = allWorkdayCertifications.find(c => c.id === item.certId)?.name ?? String(item.certId);
                    return `${empName} – ${certName}`;
                });
                warningToast(
                    <div>
                        <div>{updateConflicts.length === 1 ? '1 certification was modified by another user:' : `${updateConflicts.length} certifications were modified by another user:`}</div>
                        <ul className='mb-0 ps-3'>
                            {items.map((item, i) => <li key={i}>{item}</li>)}
                        </ul>
                        <div className='mt-1'>Please refresh and try again.</div>
                    </div>
                );
            }

            if (failed.length > 0) {
                const detail = failed.map(r =>
                    `HTTP ${r.status || 'error'}${r.statusText ? ': ' + r.statusText : ''}${r.data?.message ? '\n' + r.data.message : r.data && typeof r.data === 'string' ? '\n' + r.data : ''}`
                ).join('\n');
                errorToast(`${failed.length} of ${pairs.length} certifications failed to save.`, { detail });
            }

            if (updatesSucceeded.length > 0) {
                const items = updatesSucceeded.map(({ item }) => {
                    const empName = batchSelectedEmployees.get(item.empId)?.employeeName ?? item.empId;
                    const certName = allWorkdayCertifications.find(c => c.id === item.certId)?.name ?? String(item.certId);
                    return { empName, certName, oldExpiry: item.oldExpiry, newExpiry: batchExpiryDate };
                });
                setUpdateConfirmItems(items);
            }

            if (conflicts.length > 0 || updateConflicts.length > 0 || failed.length > 0) {
                if ((succeeded.length > 0 || updatesSucceeded.length > 0) && onSaveSuccess) await onSaveSuccess();
                return;
            }

            setBatchModalOpen(false);
            if (onSaveSuccess) await onSaveSuccess();
        } catch {
            errorToast('An unexpected error occurred while saving certifications.');
        } finally {
            setSaving(false);
        }
    };

    const handleManageSave = async () => {
        if (!manageEmployee) return;
        if (isMocked) {
            infoToast('You have clicked on the "Save" button.  This has been disabled because you are currently not yourself.');
            return;
        }

        setSaving(true);
        try {
            const ops: { label: string; promise: ReturnType<typeof removeEmployeeCertification> }[] = [];

            for (const ec of manageEmployee.certs) {
                const edit = manageCertEdits.get(ec.id);
                if (!edit) continue;
                if (edit.remove) {
                    ops.push({ label: `Remove: ${ec.certification.name}`, promise: removeEmployeeCertification(ec.id, userEmail) });
                } else {
                    const origIssued = ec.issuedDate ? ec.issuedDate.slice(0, 10) : '';
                    const origExpiry = ec.expirationDate ? ec.expirationDate.slice(0, 10) : '';
                    if (edit.issuedDate !== origIssued || edit.expiryDate !== origExpiry) {
                        ops.push({ label: `Update: ${ec.certification.name}`, promise: updateEmployeeCertification(ec.id, edit.issuedDate || null, edit.expiryDate || null, userEmail, ec.rowVersion) });
                    }
                }
            }

            const employeeId = parseInt(manageEmployee.employee.employeeID);
            manageAddIds.forEach(certId => {
                const cert = allWorkdayCertifications.find(c => c.id === certId);
                ops.push({ label: `Add: ${cert?.name ?? certId}`, promise: certifyEmployee(employeeId, certId, manageAddIssuedDate, manageAddExpiryDate, manageAddCertifiedById!, userEmail) });
            });

            if (ops.length === 0) {
                setManageEmployee(null);
                return;
            }

            const results = await Promise.all(ops.map(o => o.promise));
            const tagged = results.map((r, i) => ({ r, label: ops[i].label }));
            const succeeded = tagged.filter(({ r }) => r.status >= 200 && r.status < 300);
            const addConflicts = tagged.filter(({ r, label }) => r.status === 409 && label.startsWith('Add:'));
            const updateConflicts = tagged.filter(({ r, label }) => r.status === 409 && label.startsWith('Update:'));
            const failed = tagged.filter(({ r }) => r.status !== 409 && (r.status < 200 || r.status >= 300));

            if (succeeded.length > 0) {
                const detail = succeeded.map(({ label }) => label).join('\n');
                successToast(
                    `${succeeded.length} change${succeeded.length !== 1 ? 's' : ''} saved for ${manageEmployee.employee.employeeName}.`,
                    { autoClose: false, detail }
                );
            }

            if (addConflicts.length > 0) {
                const empName = manageEmployee.employee.employeeName;
                const certNames = addConflicts.map(({ label }) => label.replace(/^Add:\s*/, ''));
                infoToast(
                    <div>
                        <div>{empName} already has {addConflicts.length === 1 ? 'this certification:' : 'these certifications:'}</div>
                        <ul className='mb-0 ps-3'>
                            {certNames.map((name, i) => <li key={i}>{name}</li>)}
                        </ul>
                    </div>,
                    { autoClose: false }
                );
            }

            if (updateConflicts.length > 0) {
                const certNames = updateConflicts.map(({ label }) => label.replace(/^Update:\s*/, ''));
                warningToast(
                    <div>
                        <div>{updateConflicts.length === 1 ? 'This certification was modified by another user:' : 'These certifications were modified by another user:'}</div>
                        <ul className='mb-0 ps-3'>
                            {certNames.map((name, i) => <li key={i}>{name}</li>)}
                        </ul>
                        <div className='mt-1'>Please refresh and try again.</div>
                    </div>
                );
            }

            if (failed.length > 0) {
                const detail = failed.map(({ r, label }) =>
                    `${label}: HTTP ${r.status || 'error'}${r.statusText ? ': ' + r.statusText : ''}${r.data?.message ? '\n  ' + r.data.message : r.data && typeof r.data === 'string' ? '\n  ' + r.data : ''}`
                ).join('\n');
                errorToast(`${failed.length} of ${results.length} operation${results.length !== 1 ? 's' : ''} failed.`, { detail });
            }

            if (addConflicts.length > 0 || updateConflicts.length > 0 || failed.length > 0) {
                if (succeeded.length > 0 && onSaveSuccess) await onSaveSuccess();
                return;
            }

            setManageEmployee(null);
            if (onSaveSuccess) await onSaveSuccess();
        } catch {
            errorToast('An unexpected error occurred while saving changes.');
        } finally {
            setSaving(false);
        }
    };


    const renderBatchModal = () => {
        const activeCerts = allWorkdayCertifications.filter(c => c.isActive);

        const availableEmployees = batchAdResults.filter(e => !batchSelectedEmployees.has(e.employeeID));

        const selectedEmployeeObjects = Array.from(batchSelectedEmployees.values());

        const availableCerts = batchCertSearch
            ? activeCerts.filter(c =>
                !batchSelectedCerts.has(c.id) &&
                certNameMatches(c.name, batchCertSearch)
            )
            : [];

        const selectedCertObjects = activeCerts.filter(c => batchSelectedCerts.has(c.id));

        const addEmployee = (emp: EmployeeDetail) =>
            setBatchSelectedEmployees(prev => new Map(prev).set(emp.employeeID, emp));

        const removeEmployee = (id: string) =>
            setBatchSelectedEmployees(prev => { const n = new Map(prev); n.delete(id); return n; });

        const addCert = (id: number) =>
            setBatchSelectedCerts(prev => new Set<number>(Array.from(prev).concat(id)));

        const removeCert = (id: number) =>
            setBatchSelectedCerts(prev => { const n = new Set<number>(Array.from(prev)); n.delete(id); return n; });

        const canSave = batchSelectedEmployees.size > 0 && batchSelectedCerts.size > 0 && !!batchIssueDate && !!batchExpiryDate && batchCertifiedById !== null;

        return (
            <Modal
                show={batchModalOpen}
                onHide={() => setBatchModalOpen(false)}
                size='xl'
                enforceFocus={false} // Fixes issue where you can't type in the search box
            >
                <Modal.Header closeButton>
                    <Modal.Title className='w-100 text-center'>Add Certifications</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ height: 600, display: 'flex', flexDirection: 'column' }}>
                    <div className='row g-3 mb-3'>
                        <div className='col-6'>
                            <div className='swt2-batch-panel'>
                                <div className='swt2-batch-panel-header'>
                                    <span>Employees <span className='text-muted fw-normal'>({batchSelectedEmployees.size} selected)</span></span>
                                    {batchSelectedEmployees.size > 0 && (
                                        <button type='button' className='btn btn-link btn-sm p-0'
                                            onClick={() => setBatchSelectedEmployees(new Map())}>
                                            Clear all
                                        </button>
                                    )}
                                </div>
                                <div className='swt2-batch-panel-search'>
                                    <input
                                        type='text'
                                        className='form-control form-control-sm'
                                        placeholder='Search employees...'
                                        value={batchEmployeeSearch}
                                        onChange={e => {
                                            const val = e.target.value;
                                            batchEmployeeSearchRef.current = val;
                                            setBatchEmployeeSearch(val);
                                            setBatchAdResults([]);
                                            if (val.trim().length > 0) {
                                                EmployeeService.getEmployees(val, undefined, SWT_ELIGIBLE_JOB_CODES)
                                                    .then(res => {
                                                        // Trust the server-side job-code filter; a client-side re-filter
                                                        // drops employees whose AD job code differs only in casing.
                                                        if (batchEmployeeSearchRef.current === val)
                                                            setBatchAdResults(res.data);
                                                    })
                                                    .catch(() => setBatchAdResults([]));
                                            }
                                        }}
                                    />
                                </div>
                                <div className='swt2-batch-results' key={batchEmployeeSearch}>
                                    {availableEmployees.length === 0
                                        ? <div className='swt2-batch-empty'>{batchEmployeeSearch ? 'No results' : 'Type to search'}</div>
                                        : availableEmployees.map((e, i) => (
                                            <div
                                                key={`${e.employeeID ?? ''}-${i}`}
                                                className='swt2-batch-result-item'
                                                onClick={() => addEmployee(e)}
                                            >
                                                {e.employeeName} <span className='text-muted small'>({e.employeeID})</span>
                                            </div>
                                        ))
                                    }
                                </div>
                                {selectedEmployeeObjects.length > 0 && (
                                    <>
                                        <div className='swt2-batch-selected-header'>Selected</div>
                                        <div className='swt2-batch-selected'>
                                            {selectedEmployeeObjects.map(e => (
                                                <div key={e.employeeID} className='swt2-batch-selected-item'>
                                                    <span>{e.employeeName} <span className='text-muted small'>({e.employeeID})</span></span>
                                                    <button type='button' className='btn p-0 border-0 bg-transparent' aria-label='Remove'
                                                        onClick={() => removeEmployee(e.employeeID)}>
                                                        <CircleX size='14' color='var(--bs-secondary-color, #6c757d)' />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        <div className='col-6'>
                            <div className='swt2-batch-panel'>
                                <div className='swt2-batch-panel-header'>
                                    <span>Certifications <span className='text-muted fw-normal'>({batchSelectedCerts.size} selected)</span></span>
                                    {batchSelectedCerts.size > 0 && (
                                        <button type='button' className='btn btn-link btn-sm p-0'
                                            onClick={() => setBatchSelectedCerts(new Set())}>
                                            Clear all
                                        </button>
                                    )}
                                </div>
                                <div className='swt2-batch-panel-search'>
                                    <input
                                        type='text'
                                        className='form-control form-control-sm'
                                        placeholder='Search certifications...'
                                        value={batchCertSearch}
                                        onChange={e => setBatchCertSearch(e.target.value)}
                                    />
                                </div>
                                <div className='swt2-batch-results'>
                                    {availableCerts.length === 0
                                        ? <div className='swt2-batch-empty'>{batchCertSearch ? 'No results' : 'Type to search'}</div>
                                        : availableCerts.map(c => (
                                            <div
                                                key={c.id}
                                                className='swt2-batch-result-item'
                                                onClick={() => addCert(c.id)}
                                            >
                                                {c.name} <span className='text-muted small'>({c.workdayReferenceId})</span>
                                            </div>
                                        ))
                                    }
                                </div>
                                {selectedCertObjects.length > 0 && (
                                    <>
                                        <div className='swt2-batch-selected-header'>Selected</div>
                                        <div className='swt2-batch-selected'>
                                            {selectedCertObjects.map(c => (
                                                <div key={c.id} className='swt2-batch-selected-item'>
                                                    <span>{c.name} <span className='text-muted small'>({c.workdayReferenceId})</span></span>
                                                    <button type='button' className='btn p-0 border-0 bg-transparent' aria-label='Remove'
                                                        onClick={() => removeCert(c.id)}>
                                                        <CircleX size='14' color='var(--bs-secondary-color, #6c757d)' />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className='row g-3 align-items-end justify-content-center mt-auto pt-3 border-top'>
                        <div className='col'>
                            <SearchableStringListDropdown
                                label='Certified By'
                                options={gsList}
                                ref={batchGsRef}
                                clearable
                                disabled={saving}
                                wrapperClassName='w-100'
                                onChange={(label: string | undefined) => {
                                    const match = label?.match(/\((\d+)\)$/);
                                    setBatchCertifiedById(match ? parseInt(match[1]) : null);
                                }}
                            />
                        </div>
                        <div className='col-auto'>
                            <label className='form-label mb-1'>Issue Date</label>
                            <input
                                type='date'
                                className='form-control'
                                value={batchIssueDate}
                                onChange={e => setBatchIssueDate(e.target.value)}
                            />
                        </div>
                        <div className='col-auto'>
                            <div className='d-flex flex-column gap-1'>
                                <FormCheck
                                    label='One Year'
                                    checked={batchIsOneYear}
                                    onChange={handleBatchSetOneYear}
                                />
                                <FormCheck
                                    label='Evergreen'
                                    checked={batchIsEvergreen}
                                    onChange={handleBatchSetEvergreen}
                                />
                            </div>
                        </div>
                        <div className='col-auto'>
                            <label className='form-label mb-1'>Expiration Date</label>
                            <input
                                type='date'
                                className='form-control'
                                value={batchExpiryDate}
                                onChange={e => setBatchExpiryDate(e.target.value)}
                            />
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <span className='text-muted small me-auto'>
                        {batchSelectedEmployees.size} employee{batchSelectedEmployees.size !== 1 ? 's' : ''} &times; {batchSelectedCerts.size} certification{batchSelectedCerts.size !== 1 ? 's' : ''}
                    </span>
                    <button type='button' className='btn btn-outline-secondary' disabled={saving} onClick={() => setBatchModalOpen(false)}>
                        Cancel
                    </button>
                    <button type='button' className='btn btn-primary' disabled={!canSave || saving} onClick={handleBatchSave}>
                        {saving ? 'Saving…' : 'Save'}
                    </button>
                </Modal.Footer>
            </Modal>
        );
    };


    const renderManageModal = () => {
        if (!manageEmployee) return null;

        const assignedCertIds = new Set(manageEmployee.certs.map(ec => ec.certification.id));
        const availableToAdd = allWorkdayCertifications.filter(c => c.isActive && !assignedCertIds.has(c.id));
        const matchedToAdd = normalizeText(manageAddSearch)
            ? availableToAdd.filter(c => certNameMatches(c.name, manageAddSearch))
            : availableToAdd;
        // Selected certs stay visible regardless of the search so they can always be
        // unchecked; the search only narrows the not-yet-selected list below them.
        const selectedToAdd = availableToAdd.filter(c => manageAddIds.has(c.id));
        const unselectedToAdd = matchedToAdd.filter(c => !manageAddIds.has(c.id));

        const updateEdit = (ecId: number, field: 'issuedDate' | 'expiryDate' | 'remove', value: string | boolean) => {
            setManageCertEdits(prev => {
                const next = new Map(prev);
                const entry = next.get(ecId);
                if (entry) next.set(ecId, { ...entry, [field]: value });
                return next;
            });
        };

        const toggleAddId = (certId: number) => {
            setManageAddIds(prev => {
                const next = new Set(prev);
                if (next.has(certId)) next.delete(certId);
                else next.add(certId);
                return next;
            });
        };

        const hasChanges = manageEmployee.certs.some(ec => {
            const edit = manageCertEdits.get(ec.id);
            if (!edit) return false;
            if (edit.remove) return true;
            return edit.issuedDate !== (ec.issuedDate ? ec.issuedDate.slice(0, 10) : '')
                || edit.expiryDate !== (ec.expirationDate ? ec.expirationDate.slice(0, 10) : '');
        }) || manageAddIds.size > 0;

        const addDatesValid = manageAddIds.size === 0 || (!!manageAddIssuedDate && !!manageAddExpiryDate && manageAddCertifiedById !== null);
        const canSave = hasChanges && addDatesValid;

        return (
            <Modal
                show={!!manageEmployee}
                onHide={() => setManageEmployee(null)}
                size='lg'
                enforceFocus={false} // Fixes issue where you can't type in the search box
            >
                <Modal.Header closeButton>
                    <Modal.Title className='w-100 text-center'>
                        Manage Certifications – {manageEmployee.employee.employeeName}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className='mb-1 fw-semibold text-muted small text-uppercase'>Current Certifications</div>
                    {manageEmployee.certs.length === 0 ? (
                        <div className='text-muted small mb-3'>No certifications assigned.</div>
                    ) : (
                        <table className='table table-sm table-borderless mb-3'>
                            <thead>
                                <tr className='text-muted small'>
                                    <th>Certification</th>
                                    <th>Issued</th>
                                    <th>Expiration</th>
                                    <th>Remove</th>
                                </tr>
                            </thead>
                            <tbody>
                                {manageEmployee.certs.map(ec => {
                                    const edit = manageCertEdits.get(ec.id) ?? { issuedDate: '', expiryDate: '', remove: false };
                                    return (
                                        <tr key={ec.id} className={edit.remove ? 'text-decoration-line-through text-muted' : ''}>
                                            <td className='align-middle'>{ec.certification.name}</td>
                                            <td>
                                                <input
                                                    type='date'
                                                    className='form-control form-control-sm'
                                                    value={edit.issuedDate}
                                                    disabled={edit.remove || saving}
                                                    onChange={e => updateEdit(ec.id, 'issuedDate', e.target.value)}
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    type='date'
                                                    className='form-control form-control-sm'
                                                    value={edit.expiryDate}
                                                    disabled={edit.remove || saving}
                                                    onChange={e => updateEdit(ec.id, 'expiryDate', e.target.value)}
                                                />
                                            </td>
                                            <td className='align-middle text-center'>
                                                <FormCheck
                                                    type='checkbox'
                                                    id={`manage-remove-${ec.id}`}
                                                    checked={edit.remove}
                                                    disabled={saving}
                                                    onChange={e => updateEdit(ec.id, 'remove', e.target.checked)}
                                                    className='text-danger'
                                                />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}

                    <hr className='my-2' />
                    <div className='d-flex align-items-center justify-content-between mb-2'>
                        <div className='fw-semibold text-muted small text-uppercase'>
                            Add Certifications <span className='fw-normal'>({manageAddIds.size} selected)</span>
                        </div>
                        {manageAddIds.size > 0 && (
                            <button type='button' className='btn btn-link btn-sm p-0' onClick={() => setManageAddIds(new Set())}>
                                Clear all
                            </button>
                        )}
                    </div>
                    {availableToAdd.length === 0 ? (
                        <div className='text-muted small'>All active certifications are already assigned.</div>
                    ) : (
                        <>
                            <input
                                type='text'
                                className='form-control form-control-sm mb-2'
                                placeholder='Search certifications...'
                                value={manageAddSearch}
                                disabled={saving}
                                onChange={e => setManageAddSearch(e.target.value)}
                            />
                            <div className='d-flex flex-column gap-1 mb-3' style={{ height: '280px', overflowY: 'auto' }}>
                                {selectedToAdd.map(c => (
                                    <FormCheck
                                        key={c.id}
                                        type='checkbox'
                                        id={`manage-add-${c.id}`}
                                        label={c.name}
                                        checked
                                        disabled={saving}
                                        onChange={() => toggleAddId(c.id)}
                                    />
                                ))}
                                {selectedToAdd.length > 0 && unselectedToAdd.length > 0 && (
                                    <hr className='my-1' />
                                )}
                                {unselectedToAdd.map(c => (
                                    <FormCheck
                                        key={c.id}
                                        type='checkbox'
                                        id={`manage-add-${c.id}`}
                                        label={c.name}
                                        checked={false}
                                        disabled={saving}
                                        onChange={() => toggleAddId(c.id)}
                                    />
                                ))}
                                {unselectedToAdd.length === 0 && manageAddSearch.trim() !== '' && (
                                    <div className='text-muted small'>No matches</div>
                                )}
                            </div>
                            <div className='row g-3 align-items-end border-top pt-3' style={{ opacity: manageAddIds.size === 0 ? 0.35 : 1 }}>
                                <div className='col'>
                                    <SearchableStringListDropdown
                                        label='Certified By'
                                        options={gsList}
                                        ref={manageGsRef}
                                        clearable
                                        disabled={saving || manageAddIds.size === 0}
                                        wrapperClassName='w-100'
                                        onChange={(label: string | undefined) => {
                                            const match = label?.match(/\((\d+)\)$/);
                                            setManageAddCertifiedById(match ? parseInt(match[1]) : null);
                                        }}
                                    />
                                </div>
                                <div className='col-auto'>
                                    <label className='form-label mb-1'>Issue Date</label>
                                    <input
                                        type='date'
                                        className='form-control'
                                        value={manageAddIssuedDate}
                                        disabled={saving || manageAddIds.size === 0}
                                        onChange={e => setManageAddIssuedDate(e.target.value)}
                                    />
                                </div>
                                <div className='col-auto'>
                                    <div className='d-flex flex-column gap-1'>
                                        <FormCheck
                                            label='One Year'
                                            checked={manageAddIsOneYear}
                                            disabled={saving || manageAddIds.size === 0}
                                            onChange={() => { setManageAddIsOneYear(true); setManageAddIsEvergreen(false); }}
                                        />
                                        <FormCheck
                                            label='Evergreen'
                                            checked={manageAddIsEvergreen}
                                            disabled={saving || manageAddIds.size === 0}
                                            onChange={() => { setManageAddIsOneYear(false); setManageAddIsEvergreen(true); }}
                                        />
                                    </div>
                                </div>
                                <div className='col-auto'>
                                    <label className='form-label mb-1'>Expiration Date</label>
                                    <input
                                        type='date'
                                        className='form-control'
                                        value={manageAddExpiryDate}
                                        disabled={saving || manageAddIds.size === 0}
                                        onChange={e => setManageAddExpiryDate(e.target.value)}
                                    />
                                </div>
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <button type='button' className='btn btn-outline-secondary' disabled={saving} onClick={() => setManageEmployee(null)}>
                        Cancel
                    </button>
                    <button type='button' className='btn btn-primary' disabled={!canSave || saving} onClick={handleManageSave}>
                        {saving ? 'Saving…' : 'Save'}
                    </button>
                </Modal.Footer>
            </Modal>
        );
    };


    const renderUpdateConfirmModal = () => (
        <Modal show={updateConfirmItems.length > 0} onHide={() => setUpdateConfirmItems([])}>
            <Modal.Header closeButton>
                <Modal.Title className='w-100 text-center'>Expiration Dates Updated</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className='text-muted small mb-2'>
                    The following employees already held these certifications. Their expiration dates have been extended.
                </p>
                <table className='table table-sm table-borderless mb-0'>
                    <thead>
                        <tr className='text-muted small'>
                            <th>Employee</th>
                            <th>Certification</th>
                            <th>Previous Expiry</th>
                            <th>New Expiry</th>
                        </tr>
                    </thead>
                    <tbody>
                        {updateConfirmItems.map((item, i) => (
                            <tr key={i}>
                                <td>{item.empName}</td>
                                <td>{item.certName}</td>
                                <td>{item.oldExpiry || '—'}</td>
                                <td>{item.newExpiry}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Modal.Body>
            <Modal.Footer>
                <button type='button' className='btn btn-primary' onClick={() => setUpdateConfirmItems([])}>
                    OK
                </button>
            </Modal.Footer>
        </Modal>
    );


    return (
        <div className='border rounded-2 shadow-sm new-swt-v2'>
            <div className='swt2-controls-bar border-bottom'>
                <div className='swt2-controls-header'>
                    <div className='d-flex gap-1 flex-shrink-0'>
                        <button
                            className='btn btn-sm btn-outline-secondary'
                            title={showFilters ? 'Fewer filters' : 'More filters'}
                            onClick={() => setShowFilters(f => !f)}
                        >
                            <ChevronDown
                                className='transition-chevron'
                                style={{ transform: showFilters ? 'rotate(0deg)' : 'rotate(-90deg)' }}
                            />
                            {' '}Filters
                        </button>
                        <button
                            title='Reset filters'
                            className='btn btn-sm btn-primary'
                            onClick={resetFilters}
                        >
                            <ResetFilter />
                        </button>
                    </div>
                    <div className='swt2-filter-primary'>
                        <div title="Search by start of any part of employee's name or their ID">
                            <WordSearchInput
                                label='Employee'
                                sortable
                                sort={employeeFilter.sort}
                                ref={employeeRef}
                                filterFillerWords={false}
                                onChange={(filter: WordSearch) => setEmployeeFilter(filter)}
                                placeholder='Filter employees...'
                            />
                        </div>
                        <div>
                            <WordSearchInput
                                label='Project Name'
                                ref={jobNameRef}
                                filterFillerWords={false}
                                onChange={(filter: WordSearch) => setJobNameFilter(filter)}
                                placeholder='Filter project name...'
                            />
                        </div>
                        <div>
                            <SearchableStringListDropdown
                                options={jobNumberList}
                                label='Job Number'
                                ref={jobRef}
                                onChange={(n: string | undefined) => setJobNumberFilter(n)}
                                placeholder='Filter...'
                            />
                        </div>
                    </div>
                    <div className='d-flex gap-1 flex-shrink-0'>
                        <button
                            className='btn btn-sm btn-primary'
                            title={expandedKeys.size > 0 ? 'Collapse all' : 'Expand all'}
                            onClick={() => expandedKeys.size > 0 ? collapseAll() : expandAll()}
                        >
                            {expandedKeys.size > 0 ? <ChevronContract /> : <ChevronExpand />}
                        </button>
                        {userCanAdd && (
                            <button
                                className='btn btn-sm btn-primary'
                                onClick={() => openBatchModal()}
                            >
                                + New
                            </button>
                        )}
                    </div>
                </div>
                <Collapse in={showFilters}>
                    <div>
                        <div className='swt2-filter-secondary'>
                            <div>
                                <SearchableStringListDropdown
                                    label='Certificate'
                                    options={certNameList}
                                    ref={certRef}
                                    clearable
                                    wrapperClassName='mb-2 w-100'
                                    onChange={(name: string | undefined) => setCertFilter(name ?? '')}
                                />
                            </div>
                            <div>
                                <SearchableStringListDropdown
                                    label='Certified By'
                                    options={gsList}
                                    ref={certifiedByRef}
                                    clearable
                                    wrapperClassName='mb-2 w-100'
                                    onChange={(name: string | undefined) => setCertifiedByFilter(name)}
                                />
                            </div>
                            <div>
                                <DateRangeDropdown
                                    label='Issued Date'
                                    ref={issuedDateRef}
                                    wrapperClassName='mb-2'
                                    buttonWidthCh={0}
                                    onChange={(d) => setIssuedDateFilter(d)}
                                />
                            </div>
                            <div>
                                <DateRangeDropdown
                                    label='Expiration Date'
                                    ref={expirationDateRef}
                                    wrapperClassName='mb-2'
                                    buttonWidthCh={0}
                                    onChange={(d) => setExpirationDateFilter(d)}
                                />
                            </div>
                        </div>
                    </div>
                </Collapse>
            </div>

            <div className='swt2-table-container'>
                <table className='swt2-table my-2'>
                    <colgroup>
                        <col /><col /><col /><col />
                    </colgroup>
                    <thead className='swt2-header'>
                        <tr>
                            <th>Employee</th>
                            <th>Project Name</th>
                            <th>Job Number</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredGroups.length > 0 ? filteredGroups.map((group, i) => {
                            const key = group.employee.employeeID;
                            const isExpanded = expandedKeys.has(key);
                            const isOdd = i % 2 === 0;
                            const rowClass = isOdd ? 'swt2-row-odd' : 'swt2-row-even';

                            return (
                                <React.Fragment key={`${key}-${i}`}>
                                    <tr
                                        data-key={key}
                                        className={`align-middle cursor-pointer ${rowClass}`}
                                        tabIndex={0}
                                        onClick={() => toggleExpand(key)}
                                        onKeyDown={onRowKeyDown}
                                        aria-expanded={isExpanded}
                                        aria-controls={`swt2-expand-${key}`}
                                        style={{ userSelect: 'none' }}
                                    >
                                        <td>{`${group.employee.employeeName} (${group.employee.employeeID})`}</td>
                                        <td>{findProjectName(projectList, group.employee.employeeLocation)}</td>
                                        <td>{group.employee.employeeLocation}</td>
                                        <td className='text-center'>
                                            <button
                                                type='button'
                                                className='btn p-0 border-0 bg-transparent'
                                                aria-expanded={isExpanded}
                                                aria-controls={`swt2-expand-${key}`}
                                                onClick={e => { e.stopPropagation(); toggleExpand(key); }}
                                            >
                                                <ChevronDown
                                                    className='transition-chevron'
                                                    style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
                                                />
                                            </button>
                                        </td>
                                    </tr>

                                    <tr className={`swt2-expanded-row ${rowClass}`}>
                                        <td colSpan={4} className='p-0'>
                                            <Collapse in={isExpanded}>
                                                <div id={`swt2-expand-${key}`} className={`border-top px-3 py-2 ${rowClass}`}>
                                                    <div className='d-flex justify-content-between align-items-center mb-2'>
                                                        <span className='text-muted small'>Assigned Certifications</span>
                                                        {userCanEdit && (
                                                            <button
                                                                type='button'
                                                                className='btn btn-sm btn-outline-primary'
                                                                onClick={e => { e.stopPropagation(); openManageModal(group); }}
                                                            >
                                                                Edit Certificates
                                                            </button>
                                                        )}
                                                    </div>
                                                    <table className='swt2-cert-table'>
                                                        <colgroup>
                                                            <col /><col /><col /><col /><col /><col />
                                                        </colgroup>
                                                        <thead>
                                                            <tr>
                                                                <th>Certification</th>
                                                                <th>Certificate Status</th>
                                                                <th>Certified By</th>
                                                                <th>Issued</th>
                                                                <th>Expiration</th>
                                                                <th></th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {group.certs.map((ec, j) => {
                                                                const certKey = ec.id;
                                                                const isCertExpanded = expandedCertKeys.has(certKey);
                                                                const certClass = j % 2 === 0 ? 'swt2-cert-odd' : 'swt2-cert-even';
                                                                return (
                                                                    <React.Fragment key={certKey}>
                                                                        <tr
                                                                            className={`cursor-pointer ${certClass}`}
                                                                            onClick={() => toggleCertExpand(certKey)}
                                                                            style={{ userSelect: 'none' }}
                                                                        >
                                                                            <td>{ec.certification.name}</td>
                                                                            <td>{ec.certification.isActive ? 'Active' : 'Inactive'}</td>
                                                                            <td>{ec.certifiedBy?.employeeName ?? '—'}</td>
                                                                            <td>{ec.issuedDate ? ec.issuedDate.slice(0, 10) : '—'}</td>
                                                                            <td>{ec.expirationDate ? ec.expirationDate.slice(0, 10) : '—'}</td>
                                                                            <td className='text-center'>
                                                                                <div className='d-flex gap-2 justify-content-center align-items-center'>
                                                                                    <button
                                                                                        type='button'
                                                                                        className='btn p-0 border-0 bg-transparent'
                                                                                        onClick={e => { e.stopPropagation(); toggleCertExpand(certKey); }}
                                                                                    >
                                                                                        <ChevronDown
                                                                                            className='transition-chevron'
                                                                                            style={{ transform: isCertExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
                                                                                        />
                                                                                    </button>
                                                                                </div>
                                                                            </td>
                                                                        </tr>

                                                                        <tr className={`swt2-cert-expanded-row ${certClass}`}>
                                                                            <td colSpan={6} className='p-0'>
                                                                                <Collapse in={isCertExpanded}>
                                                                                    <div className='px-3 py-2 border-top'>
                                                                                        <div className='d-flex align-items-center mb-2'>
                                                                                            <span className='text-muted small'>Topics</span>
                                                                                        </div>
                                                                                        <table className='swt2-detail-table'>
                                                                                            <colgroup>
                                                                                                <col /><col /><col />
                                                                                            </colgroup>
                                                                                            <thead>
                                                                                                <tr>
                                                                                                    <th>Topic</th>
                                                                                                    <th>Description</th>
                                                                                                    <th>Status</th>
                                                                                                </tr>
                                                                                            </thead>
                                                                                            <tbody>
                                                                                                {ec.certification.topics.map((t, k) => (
                                                                                                    <tr
                                                                                                        key={t.id}
                                                                                                        className={k % 2 === 0 ? 'swt2-detail-odd' : 'swt2-detail-even'}
                                                                                                    >
                                                                                                        <td>{t.name}</td>
                                                                                                        <td>{t.description ?? '—'}</td>
                                                                                                        <td>{t.status == null ? '—' : t.status ? 'Active' : 'Inactive'}</td>
                                                                                                    </tr>
                                                                                                ))}
                                                                                            </tbody>
                                                                                        </table>
                                                                                    </div>
                                                                                </Collapse>
                                                                            </td>
                                                                        </tr>
                                                                    </React.Fragment>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </Collapse>
                                        </td>
                                    </tr>
                                </React.Fragment>
                            );
                        }) : (
                            <tr>
                                <td colSpan={4} className='px-2'>
                                    <div className='text-muted'>None</div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            {renderBatchModal()}
            {renderManageModal()}
            {renderUpdateConfirmModal()}
        </div>
    );
};

export default StandardWorkTrainerTable;
